import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCallback, useMemo } from "react";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";

export type FeatureKey =
  | "crm"
  | "financeiro"
  | "instalacoes"
  | "whatsapp"
  | "portal_cliente"
  | "ai_insights"
  | "relatorios"
  | "relatorios_avancados"
  | "api_access"
  | "white_label"
  | "sales_page"
  | "auto_proposta"
  | "contratos"
  | "dimensionamento"
  | "conciliacao_bancaria"
  | "comissoes"
  | "importacao_csv"
  | "dre"
  | "multi_pipeline"
  | "suprimentos"
  | "engenharia"
  | "atividades"
  | "automacoes_atividades"
  | "cadencias_followup"
  | "templates_atividades"
  | "logistica"
  | "dossie"
  | "calculadora_simplificada"
  | "calculadora_avancada"
  | "permissoes_secoes"
  | "visibilidade_documentos"
  | "max_users"
  | "max_leads"
  | "max_deals"
  | "max_storage_mb"
  | "max_propostas"
  | "max_contratos"
  | "max_whatsapp_mes"
  | "max_pipelines"
  | "whatsapp_hub"
  | "prospeccao_bdr"
  | "agentes_ia"
  | "whatsapp_meta"
  | "auto_deal_creation"
  | "agente_sdr"
  | "ui_customization"
  | "cotacao_rapida"
  | "cotacao_analise_financeira"
  | "cotacao_modo_cpq";

interface SubscriptionInfo {
  status: string | null;
  planName: string | null;
  trialEndsAt: string | null;
}

export function useTenantPlan() {
  const { currentTenant } = useAuth();
  const { isSuperAdmin, isLoading: isSuperAdminLoading } = useSuperAdmin();
  const tenantId = currentTenant?.id;

  // Fetch features
  const { data: features = {}, isLoading: isFeaturesLoading } = useQuery({
    queryKey: ["tenant-features", tenantId],
    queryFn: async () => {
      if (!tenantId) return {};
      const { data, error } = await supabase.rpc("get_tenant_features", {
        p_tenant_id: tenantId,
      });
      if (error) {
        console.error("get_tenant_features error:", error);
        return {};
      }
      return (data as Record<string, boolean | number>) ?? {};
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch subscription info
  const { data: subscription } = useQuery<SubscriptionInfo>({
    queryKey: ["tenant-subscription-info", tenantId],
    queryFn: async () => {
      if (!tenantId) return { status: null, planName: null, trialEndsAt: null };
      const { data, error } = await supabase
        .from("tenant_subscriptions")
        .select("status, trial_ends_at, plan:plans(name)")
        .eq("tenant_id", tenantId)
        .in("status", ["active", "trialing"])
        .limit(1)
        .maybeSingle();

      if (error || !data) return { status: null, planName: null, trialEndsAt: null };
      const plan = data.plan as unknown as { name: string } | null;
      return {
        status: data.status,
        planName: plan?.name ?? null,
        trialEndsAt: (data as any).trial_ends_at ?? null,
      };
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  const hasFeature = useCallback(
    (key: FeatureKey): boolean => {
      // Super admin always has access
      if (isSuperAdmin) return true;
      // No features configured = block access (safe default)
      if (!features || Object.keys(features).length === 0) return false;
      const val = features[key];
      if (val === undefined) return false;
      if (typeof val === "boolean") return val;
      if (typeof val === "number") return val > 0;
      return false;
    },
    [features, isSuperAdmin],
  );

  const checkLimit = useCallback(
    async (key: FeatureKey) => {
      if (!tenantId) return { current: 0, limit: null, allowed: true };
      const { data, error } = await supabase.rpc("check_tenant_limit", {
        p_tenant_id: tenantId,
        p_feature_key: key,
      });
      if (error) return { current: 0, limit: null, allowed: true };
      return data as { current: number; limit: number | null; allowed: boolean };
    },
    [tenantId],
  );

  const isTrialing = subscription?.status === "trialing";
  const planName = subscription?.planName ?? "Sem plano";

  const daysUntilExpiry = useMemo(() => {
    if (!subscription?.trialEndsAt) return null;
    const diff = new Date(subscription.trialEndsAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [subscription?.trialEndsAt]);

  return {
    features,
    hasFeature,
    checkLimit,
    planName,
    isTrialing,
    isLoading: isFeaturesLoading || isSuperAdminLoading,
    daysUntilExpiry,
    subscriptionStatus: subscription?.status ?? null,
  };
}
